import {
  FaFacebookF,
  FaTwitter,
  FaInstagram,
  FaLinkedinIn,
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaEnvelope,
} from "react-icons/fa";
import { useRef, useEffect, useState } from "react";

// Intersection Observer hook for on-screen detection
function useOnScreen(ref, rootMargin = "0px") {
  const [isIntersecting, setIntersecting] = useState(false);

  useEffect(() => {
    if (!ref.current) return;
    const observer = new window.IntersectionObserver(
      ([entry]) => setIntersecting(entry.isIntersecting),
      { rootMargin }
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [ref, rootMargin]);

  return isIntersecting;
}

export default function Footer() {
  const footerRef = useRef(null);
  const [hasAnimated, setHasAnimated] = useState(false);

  // Animation triggers when footer is visible by 60px
  const isVisible = useOnScreen(footerRef, "-60px");

  useEffect(() => {
    if (isVisible && !hasAnimated) setHasAnimated(true);
  }, [isVisible, hasAnimated]);

  // Animation style
  const animStyle = (delay = 0, duration = 1100, initialY = 36) => ({
    transition: `all ${duration}ms cubic-bezier(0.33,0.75,0.68,1)`,
    transitionDelay: hasAnimated ? `${delay}ms` : "0ms",
    opacity: hasAnimated ? 1 : 0,
    transform: hasAnimated ? "translateY(0)" : `translateY(${initialY}px)`,
    willChange: "opacity, transform",
  });

  // Slightly stagger animations per section
  return (
    <footer
      className="font-PTSerif-Regular"
      ref={footerRef}
      style={{ position: "relative", zIndex: 1 }}
    >
      {/* CTA SECTION */}
      <div
        className="bg-blue-500 text-white py-10 font-PTSerif-Regular"
        style={animStyle(0, 1100, 38)}
      >
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row justify-between items-center gap-6 font-PTSerif-Regular">
          <div>
            <h2 className="text-2xl font-semibold font-PTSerif-Regular">
              Do you need assistance with IT services or computer repair?
            </h2>

            <p className="text-sm mt-2 text-blue-100 font-PTSerif-Regular">
              STRALIA PRIVATE LIMITED provides Networking AMC, Computer AMC,
              Laptop AMC, Server AMC, Projector AMC and CCTV installation
              services for businesses and offices.
            </p>
          </div>
          <a
            href="/contact"
            className="bg-gray-800 px-6 py-3 rounded text-white font-semibold hover:bg-gray-900 transition whitespace-nowrap font-PTSerif-Regular"
            style={animStyle(120, 1000, 24)}
          >
            CONTACT WITH US
          </a>
        </div>
      </div>

      {/* MAIN FOOTER */}
      <div
        className="bg-gray-100 py-20 font-PTSerif-Regular"
        style={animStyle(180, 1600, 52)}
      >
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-3 gap-12 font-PTSerif-Regular">
          {/* COMPANY INFO */}
          <div style={animStyle(220, 1000, 38)}>
            <h3 className="text-2xl font-bold text-gray-900 mb-4 font-PTSerif-Regular">
              STRALIA PRIVATE LIMITED
            </h3>
            <p className="text-gray-600 text-sm leading-relaxed mb-6 font-PTSerif-Regular">
              We provide professional IT services including Networking AMC,
              Computer AMC, Laptop AMC, Server AMC, Projector AMC,
              CCTV AMC and CCTV Installation for offices and businesses.
            </p>
            {/* Social Icons */}
            <div className="flex gap-3 font-PTSerif-Regular">
              <div
                className="bg-blue-500 p-3 text-white rounded cursor-pointer"
                style={animStyle(300, 800, 24)}
              >
                <FaFacebookF />
              </div>
              <div
                className="bg-blue-500 p-3 text-white rounded cursor-pointer"
                style={animStyle(340, 800, 24)}
              >
                <FaTwitter />
              </div>
              <div
                className="bg-blue-500 p-3 text-white rounded cursor-pointer"
                style={animStyle(380, 800, 24)}
              >
                <FaInstagram />
              </div>
              <div
                className="bg-blue-500 p-3 text-white rounded cursor-pointer"
                style={animStyle(420, 800, 24)}
              >
                <FaLinkedinIn />
              </div>
            </div>
          </div>

          {/* OPENING HOURS */}
          <div style={animStyle(260, 1000, 42)}>
            <h3 className="text-xl font-semibold mb-6 font-PTSerif-Regular">
              Opening Hours
            </h3>
            <div className="space-y-4 text-sm text-gray-600 font-PTSerif-Regular">
              <div className="flex justify-between border-b pb-2 font-PTSerif-Regular">
                <span>Monday – Saturday</span>
                <span className="bg-blue-500 text-white px-3 py-1 text-xs rounded font-PTSerif-Regular">
                  9:00 AM – 7:00 PM
                </span>
              </div>
              <div className="flex justify-between border-b pb-2 font-PTSerif-Regular">
                <span>Sunday</span>
                <span className="bg-gray-400 text-white px-3 py-1 text-xs rounded font-PTSerif-Regular">
                  Closed
                </span>
              </div>
            </div>
          </div>

          {/* CONTACT INFO */}
          <div style={animStyle(300, 1000, 42)}>
            <h3 className="text-xl font-semibold mb-6 font-PTSerif-Regular">
              Contact Us
            </h3>
            <div className="space-y-5 text-sm text-gray-600 font-PTSerif-Regular">
              <div className="flex gap-3 items-start font-PTSerif-Regular">
                <FaMapMarkerAlt className="text-blue-500 mt-1" />
                <p>
                  Office No. 179-A First Floor (B/P),  
                  Jivan Nagar, Ashram, South East Delhi - 110014
                </p>
              </div>
              <div className="flex gap-3 items-center font-PTSerif-Regular">
                <FaPhoneAlt className="text-blue-500" />
                <p>
                  9217208800 <br />
                  9217209900 <br />
                  9217204400
                </p>
              </div>
              <div className="flex gap-3 items-center font-PTSerif-Regular">
                <FaEnvelope className="text-blue-500" />
                <p>straliapvtltd@gmail.com</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* COPYRIGHT AND TECH PARTNER */}
      <div
        className="bg-white border-t py-6 text-center text-gray-600 text-sm flex flex-col md:flex-row items-center justify-center gap-2 font-PTSerif-Regular"
        style={animStyle(500, 1000, 24)}
      >
        <span>
          © {new Date().getFullYear()} STRALIA PRIVATE LIMITED. All Rights Reserved.
        </span>
        <span className="mx-2 hidden md:inline-block">|</span>
        <span>
          Tech Partner -{" "}
          <a
            href="https://gowappily.in"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline font-semibold font-PTSerif-Regular"
          >
            GoWappily Infotech
          </a>{" "}
          <span className="text-gray-400 font-PTSerif-Regular">(gowappily.in)</span>
        </span>
      </div>
    </footer>
  );
}